import { useEffect, useState } from 'react';
import './ServersPage.css';
import Loader from '../../components/Loader/Loader.jsx';
import { listServers, deleteServer } from '../../api/serversAPI/serversAPI.js';
import Pagination from '../../components/Pagination/Pagination.jsx';
import useLang from '../../hooks/use-lang/useLang.js';

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 6;

function ServersPage({ onNavigate, onLogout }) {
  const { t } = useLang();
  const [search, setSearch] = useState('');
  const [detailsServer, setDetailsServer] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteInFlight, setDeleteInFlight] = useState(false);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const handle = setTimeout(() => {
      setLoading(true);
      listServers({ search })
        .then((data) => {
          setServers(data);
          setError(null);
        })
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [search]);

  function closeServerDetails() {
    setDetailsServer(null);
  }

  function requestDelete(server) {
    setPendingDelete(server);
    setDeleteReason('');
  }

  function cancelDelete() {
    setPendingDelete(null);
    setDeleteReason('');
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    const serverId = pendingDelete.id;
    setDeleteInFlight(true);
    try {
      await deleteServer(serverId);
      setServers((prev) => prev.filter((server) => server.id !== serverId));
      setPendingDelete(null);
      setDeleteReason('');
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleteInFlight(false);
    }
  }

  useEffect(() => {
    setPage(1);
  }, [search]);

  const totalPages = Math.ceil(servers.length / PAGE_SIZE);
  const filteredServers = servers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="servers__page">
      <div className="page__container">
        <div className="servers__page__header">
          <div className="search__input__container">
            <input
              className="servers__search"
              type="text"
              placeholder={t('servers.searchPlaceholder')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label={t('servers.searchAria', 'Search servers')}
            />
          </div>
        </div>

        {loading ? (
          <Loader text={t('servers.loading')} />
        ) : (
        <div className="serversPage__card__container">
          {!loading && error && <p className="servers__empty">{t('common.error')}: {error}</p>}
          {!loading && !error && filteredServers.length === 0 && (
            <div className="servers__empty">
              <svg className="servers__empty-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
                <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
                <line x1="6" y1="6" x2="6.01" y2="6" />
                <line x1="6" y1="18" x2="6.01" y2="18" />
              </svg>
              <span className="servers__empty-title">{t('servers.empty')}</span>
              <span className="servers__empty-hint">{t('servers.emptyHint')}</span>
            </div>
          )}
          {!loading && !error && filteredServers.map((server) => (
            <div key={server.id} className="servers_card">
              <div className="server__info__container">
                <div className="server__info__row">
                  <span className="server__info__label">{t('common.name')}</span>
                  <span className="server__info__value">{server.name}</span>
                </div>
                <div className="server__info__row">
                  <span className="server__info__label">{t('servers.owner')}</span>
                  <span className="server__info__value">{server.owner}</span>
                </div>
                <div className="server__info__row">
                  <span className="server__info__label">{t('common.location')}</span>
                  <span className="server__info__value">{server.location}</span>
                </div>
                <div className="server__info__row">
                  <span className="server__info__label">IP</span>
                  <span className="server__info__value">{server.ip}</span>
                </div>
                <div className="server__info__row">
                  <span className="server__info__label">{t('common.status')}</span>
                  <span className={`server__info__value ${server.status === 'online' ? 'server__status--online' : server.status === 'stopped' ? 'server__status--stopped' : 'server__status--error'}`}>
                    {t(`common.${server.status}`, server.status)}
                  </span>
                </div>
              </div>

              <div className="servers__stats__bardge">
                  <div className="server__info__row_CPU">
                    <span className="server__info__label">CPU</span>
                    <span className="server__info__value">{server.cpu} vCPU</span>
                  </div>
                  <div className="server__info__row_RAM">
                    <span className="server__info__label">RAM</span>
                    <span className="server__info__value">{server.ram} GB</span>
                  </div>
                </div>

              <div className="servers__controller__container">
                <button className="servers__btn servers__btn--delete" type="button" onClick={() => requestDelete(server)}>{t('common.delete')}</button>
                <button className="servers__btn servers__btn--details" type="button" onClick={() => setDetailsServer(server)}>{t('common.details')}</button>
              </div>
            </div>
          ))}
        </div>
        )}

        {!loading && !error && (
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        )}
      </div>

      {detailsServer && (
        <div className="server-modal-overlay" onClick={closeServerDetails}>
          <div className="server-modal server-modal--details" onClick={(e) => e.stopPropagation()}>
            <h3 className="server-modal-title">{t('servers.detailsTitle')}</h3>
            <div className="server-modal-sections">
              <div className="server-modal-section">
                <span className="server-modal-section-title">{t('servers.serverInfo')}</span>
                <div className="server-modal-grid">
                  <div className="server-modal-row">
                    <span className="server-modal-label">{t('servers.owner')}</span>
                    <span className="server-modal-value">{detailsServer.owner}</span>
                  </div>
                  <div className="server-modal-row">
                    <span className="server-modal-label">{t('common.provider')}</span>
                    <span className="server-modal-value">{detailsServer.provider}</span>
                  </div>
                  <div className="server-modal-row">
                    <span className="server-modal-label">{t('servers.providerId')}</span>
                    <span className="server-modal-value">{detailsServer.providerId}</span>
                  </div>
                  <div className="server-modal-row">
                    <span className="server-modal-label">{t('common.plan')}</span>
                    <span className="server-modal-value">{detailsServer.plan}</span>
                  </div>
                  <div className="server-modal-row">
                    <span className="server-modal-label">IP</span>
                    <span className="server-modal-value">{detailsServer.ip}</span>
                  </div>
                  <div className="server-modal-row">
                    <span className="server-modal-label">{t('common.os')}</span>
                    <span className="server-modal-value">{detailsServer.os}</span>
                  </div>
                  <div className="server-modal-row">
                    <span className="server-modal-label">{t('common.status')}</span>
                    <span className={`server-modal-value server-modal-status--${detailsServer.status}`}>{t(`common.${detailsServer.status}`, detailsServer.status)}</span>
                  </div>
                  <div className="server-modal-row">
                    <span className="server-modal-label">{t('common.created')}</span>
                    <span className="server-modal-value">{detailsServer.created}</span>
                  </div>
                  <div className="server-modal-row">
                    <span className="server-modal-label">{t('common.expires')}</span>
                    <span className="server-modal-value">{detailsServer.expires}</span>
                  </div>
                </div>
              </div>

              <div className="server-modal-section">
                <span className="server-modal-section-title">{t('common.billing')}</span>
                <div className="server-modal-grid">
                  <div className="server-modal-row">
                    <span className="server-modal-label">{t('servers.paid')}</span>
                    <span className="server-modal-value">{`$${detailsServer.paid.toFixed(2)}`}</span>
                  </div>
                  <div className="server-modal-row">
                    <span className="server-modal-label">{t('servers.cost')}</span>
                    <span className="server-modal-value">{`$${detailsServer.cost.toFixed(2)}`}</span>
                  </div>
                  <div className="server-modal-row">
                    <span className="server-modal-label">{t('common.profit')}</span>
                    <span className="server-modal-value server-modal-profit">{`~$${(detailsServer.paid - detailsServer.cost).toFixed(2)}`}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="server-modal-actions">
              <button className="server-modal-btn server-modal-btn--close" type="button" onClick={closeServerDetails}>{t('common.close')}</button>
            </div>
          </div>
        </div>
      )}

      {pendingDelete && (
        <div className="server-modal-overlay" onClick={cancelDelete}>
          <div className="server-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="server-modal-title">{t('servers.deleteTitle')}</h3>
            <p className="server-modal-text">
              {t('servers.deleteText').replace('{name}', pendingDelete.name).replace('{ip}', pendingDelete.ip)}
            </p>
            <label className="server-modal-field">
              <span className="server-modal-field-label">{t('common.reason')}</span>
              <input
                className="server-modal-input"
                type="text"
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                disabled={deleteInFlight}
                placeholder={t('servers.reasonPlaceholder')}
                autoFocus
              />
            </label>
            <div className="server-modal-actions">
              <button
                className="server-modal-btn server-modal-btn--close"
                type="button"
                onClick={cancelDelete}
                disabled={deleteInFlight}
              >
                {t('common.cancel')}
              </button>
              <button
                className="server-modal-btn server-modal-btn--delete"
                type="button"
                onClick={confirmDelete}
                disabled={deleteInFlight || deleteReason.trim() === ''}
              >
                {deleteInFlight ? t('common.deleting') : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ServersPage;